import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { checkAuth } from './features/Auth/authActions'
import Posts from './features/Posts/Posts'
import Login from './features/Auth/Login'
import Profile from './features/Profile/Profile'
import Register from './features/Register/Register'
import RegisterSuccess from './features/Register/RegisterSuccess'
import Favorites from './features/Favorites/Favorites'
import Appointments from './features/Appointments/Appointments'
import { Box, Container, CssBaseline, ThemeProvider, createTheme, useMediaQuery } from '@mui/material'

function useSystemTheme() {
  // Prefer system theme on first load
  return useMediaQuery('(prefers-color-scheme: dark)') ? 'dark' : 'light'
}

function getStoredTheme() {
  return localStorage.getItem('themeMode')
}

function setStoredTheme(mode) {
  localStorage.setItem('themeMode', mode)
}

const getTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: {
      main: '#FF385C',
      light: '#FF6B83',
      dark: '#D4003A',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00C9B1',
      light: '#33D4C0',
      dark: '#008C7E',
      contrastText: '#ffffff',
    },
    background: {
      default: mode === 'dark' ? '#1C1C1E' : '#F5F5F7',
      paper: mode === 'dark' ? '#2C2C2E' : '#FFFFFF',
    },
    success: { main: '#21d07a' },
    error: { main: '#fd5068' },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h4: { fontWeight: 800, letterSpacing: '-0.5px' },
    h5: { fontWeight: 700, letterSpacing: '-0.3px' },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: 0,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 8, fontWeight: 500 } },
    },
    MuiCard: {
      styleOverrides: { root: { borderRadius: 16 } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 24 } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
  },
})

const PrivateRoute = ({ children, isAuthenticated }) => {
  return isAuthenticated ? children : <Navigate to="/login" state={{ from: window.location.pathname }} />
}

function MainLayout({ children, mode = 'light', toggleTheme }) {
  // Wheel listener to prevent zooming with ctrl+wheel
  React.useEffect(() => {
    const wheelListener = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.deltaY !== 0 || e.deltaX !== 0)) {
        e.preventDefault()
      }
    }
    window.addEventListener('wheel', wheelListener, { passive: false })
    return () => window.removeEventListener('wheel', wheelListener)
  }, [])

  return (
    <Box sx={{
      display: 'flex',
      bgcolor: 'background.default',
      minHeight: '100vh',
      overflow: 'hidden',
      '& *': {
        '&::-webkit-scrollbar': {
          display: 'none',
          width: 0,
          height: 0,
          background: 'transparent'
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch'
      }
    }}>
      <CssBaseline />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: '100%',
          overflow: 'hidden',
          bgcolor: 'background.default',
          '& *': {
            '&::-webkit-scrollbar': {
              display: 'none',
              width: 0,
              height: 0,
              background: 'transparent'
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch'
          }
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            height: '100vh',
            bgcolor: 'background.paper',
            boxShadow: 1,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'auto',
            '& *': {
              '&::-webkit-scrollbar': {
                display: 'none',
                width: 0,
                height: 0,
                background: 'transparent'
              },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              WebkitOverflowScrolling: 'touch'
            }
          }}
        >
          {children}
        </Container>
      </Box>
    </Box>
  )
}

function App() {
  const dispatch = useDispatch()
  const { isAuthenticated } = useSelector(state => state.auth || {})

  // Theme mode state
  const systemTheme = useSystemTheme()
  const [mode, setMode] = React.useState(getStoredTheme() || systemTheme)

  React.useEffect(() => {
    setStoredTheme(mode)
  }, [mode])

  React.useEffect(() => {
    // Check if user is authenticated on initial load
    dispatch(checkAuth())
  }, [dispatch])

  const toggleTheme = React.useCallback(() => {
    setMode(prev => (prev === 'light' ? 'dark' : 'light'))
  }, [])

  return (
    <ThemeProvider theme={getTheme(mode)}>
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/posts" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/login"
            element={<Login />}
          />
          <Route
            path="/register"
            element={<Register />}
          />
          <Route
            path="/register/done"
            element={<RegisterSuccess />}
          />
          <Route
            path="/posts/*"
            element={
              <PrivateRoute isAuthenticated={isAuthenticated}>
                <MainLayout mode={mode} toggleTheme={toggleTheme}>
                  <Posts />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute isAuthenticated={isAuthenticated}>
                <MainLayout mode={mode}>
                  <Profile toggleTheme={toggleTheme} />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/favorites"
            element={
              <PrivateRoute isAuthenticated={isAuthenticated}>
                <MainLayout mode={mode}>
                  <Favorites />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/appointments"
            element={
              <PrivateRoute isAuthenticated={isAuthenticated}>
                <MainLayout mode={mode}>
                  <Appointments />
                </MainLayout>
              </PrivateRoute>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  )
}

export default App
